import { useSyncExternalStore } from 'react'
import { APP_VERSION } from './version'

/**
 * Client version floors, the SPA half (plans/HOCUSPOCUS_UPGRADE.md §3; the backend half is
 * backend/domain/client-floor.ts — see docs/spec/60-clients.md "Client version floors").
 *
 * The SPA declares its BUNDLE version on every /v1 call (`X-Niobe-Spa-Version`, via
 * lib/desktop.ts#clientHeaders) and on every room connection (the provider parameter `spa`, via
 * `roomParameters()`). The backend answers every /v1 response with the floors it enforces, when
 * set; `noteFloorHeaders` reads them. Below a floor — or on a room refused with `client_outdated` —
 * this tab is OUTDATED for the rest of its life: no room may stay open (Canvas closes its provider
 * and does not reopen it; a bid-wide replace opens none), and ClientOutdatedDialog blocks the UI
 * until the member reloads (browser) or restarts into the update (Desktop). Nothing about it is
 * reversible in place — the bundle that is running cannot become newer without a reload.
 *
 * Imports nothing from the app beyond lib/version, so lib/api and the editor can both use it.
 */

/** Response headers the backend advertises the floors in (backend/domain/client-floor.ts). */
export const SPA_MIN_VERSION_HEADER = 'X-Niobe-Spa-Min-Version'
export const DESKTOP_MIN_VERSION_HEADER = 'X-Niobe-Desktop-Min-Version'
/** Request header carrying this bundle's version. */
export const SPA_VERSION_HEADER = 'X-Niobe-Spa-Version'
/** The WS refusal reason for a bundle below SPA_MIN_VERSION. */
export const CLIENT_OUTDATED = 'client_outdated'

/** Which part is below its floor: the SPA bundle, or (inside the Desktop) the shell itself. */
export type Outdated = 'spa' | 'desktop' | null

/** Parse a numeric `x.y.z` (a leading `v` and a `-pre`/`+build` suffix tolerated) — the same rule
 *  as the backend's `parseVersion`, so both sides agree on what is below a floor. */
export function parseVersion(raw: string | null | undefined): [number, number, number] | null {
  if (typeof raw !== 'string') return null
  const m = /^v?(\d{1,9})\.(\d{1,9})\.(\d{1,9})(?:[-+].*)?$/.exec(raw.trim())
  if (!m) return null
  return [Number(m[1]), Number(m[2]), Number(m[3])]
}

/**
 * Whether `declared` satisfies `floor`. Mirrors the backend's `meetsFloor`, with one deliberate
 * difference: a declared version this side cannot parse is NOT treated as outdated. The backend is
 * the gate (it refuses the room); a client blocking ITSELF on a version string it failed to read
 * would be a self-inflicted outage, so this only ever acts on a comparison it can actually make.
 */
export function meetsFloor(declared: string | null | undefined, floor: string | null | undefined): boolean {
  const min = parseVersion(floor)
  if (!min) return true
  const v = parseVersion(declared)
  if (!v) return true
  if (v[0] !== min[0]) return v[0] > min[0]
  if (v[1] !== min[1]) return v[1] > min[1]
  return v[2] >= min[2]
}

let outdated: Outdated = null
const listeners = new Set<() => void>()

/** The current verdict. Synchronous, so non-render code (a provider factory) can consult it. */
export function clientOutdated(): Outdated {
  return outdated
}

/** Subscribe to the verdict changing; returns the unsubscriber. */
export function onClientOutdated(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/** React view of the verdict. */
export function useClientOutdated(): Outdated {
  return useSyncExternalStore(onClientOutdated, clientOutdated, clientOutdated)
}

/**
 * Mark this tab outdated. Latches: the first verdict stands, and a shell below its floor wins over
 * a bundle below its own (a new bundle cannot fix an old shell, a shell update fixes both).
 *
 * Inside the Desktop, ask the shell to check BOTH update lanes now — the member is blocked until an
 * update lands, so waiting for the next interval check is not acceptable. An older shell has no
 * `updates.check`: the dialog then simply asks for a restart, which runs the startup check anyway.
 */
export function markClientOutdated(which: 'spa' | 'desktop'): void {
  const next: Outdated = outdated === 'desktop' ? 'desktop' : which
  if (next === outdated) return
  const first = outdated === null
  outdated = next
  if (first) {
    try {
      void window.niobeDesktop?.updates?.check?.().catch(() => undefined)
    } catch {
      /* an IPC failure must not stop the block from showing */
    }
  }
  listeners.forEach((cb) => cb())
}

/**
 * Read the floors off a /v1 response. Absent headers (no floor set) change nothing. The Desktop
 * floor applies only inside the Desktop, against the SHELL's version (`niobeDesktop.appVersion`);
 * a shell that does not report its version is left alone (nothing to compare).
 */
export function noteFloorHeaders(headers: Headers | null | undefined): void {
  if (!headers || typeof headers.get !== 'function') return
  const spaFloor = headers.get(SPA_MIN_VERSION_HEADER)
  const desktopFloor = headers.get(DESKTOP_MIN_VERSION_HEADER)
  if (!spaFloor && !desktopFloor) return
  const desktop = window.niobeDesktop
  if (desktop?.isDesktop && desktopFloor && !meetsFloor(desktop.appVersion, desktopFloor)) {
    markClientOutdated('desktop')
  }
  if (spaFloor && !meetsFloor(APP_VERSION, spaFloor)) markClientOutdated('spa')
}

/** The provider parameters every room connection carries: the bundle version the backend's
 *  SPA_MIN_VERSION is enforced against on the handshake. */
export function roomParameters(): { spa: string } {
  return { spa: APP_VERSION }
}

/** A room refusal reason meaning "this bundle is below the floor". */
export function isOutdatedRefusal(reason: string | null | undefined): boolean {
  return typeof reason === 'string' && reason.startsWith(CLIENT_OUTDATED)
}

/** Test-only: forget the verdict. */
export function resetClientOutdatedForTests(): void {
  outdated = null
  listeners.forEach((cb) => cb())
}
